import React, { FC, useEffect, useMemo, useState } from 'react';

import queryString from 'query-string';
import { useNavigate } from 'react-router-dom';

import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';

import { useNHLStatsQuery } from '../../../../redux/ducks/NHLStats';
import { PageVars, Record } from '../../../../types/RecordCategoriesTypes';
import { Option } from '../../../../types/Types';
import VideoModal from '../../../components/videoModal';
import { RecordOptions } from '../types';

import RecordTable from './RecordTable';
import utils from './utils';

type Props = {
    record: Record;
    page: string;
    pageVars: PageVars;
    recordSelectOptions: Option[];
    recordTypeSelectOptions: Option[];
    templateKey: string;
    selectedCategoryValue: any;
    selectedRecordTypeValue: any;
    options?: RecordOptions;
};

const LAST_NAME_SORT = '{"property":"lastName","direction":"ASC"}';
const LAST_NAME_CASE_INSENSITIVE_SORT = '{"property":"lastName","direction":"ASC_CI"}';

const RecordTableDataProvider: FC<Props> = ({
    record,
    page,
    pageVars,
    recordSelectOptions,
    recordTypeSelectOptions,
    templateKey,
    selectedCategoryValue,
    selectedRecordTypeValue,
    options,
}) => {
    const [currentPage, setCurrentPage] = useState(page);
    const [videoId, setVideoId] = useState(null);
    const navigate = useNavigate();

    // We could replace this sort in the DB, however when new pages are created they could run into this again.
    const restUrl = record.recordToFranchise[0].restUrl.replace(LAST_NAME_SORT, LAST_NAME_CASE_INSENSITIVE_SORT);
    const hookOptions = useMemo(
        () => ({
            record,
            url: restUrl,
        }),
        [record]
    );
    const { data: NHLStats = [], isFetching } = useNHLStatsQuery(hookOptions);

    useEffect(() => {
        if (pageVars.recordDescription === undefined || pageVars.recordDescription === '') {
            navigate(`${page}${pageVars.recordDescription === '' ? '' : '/'}${record.descriptionKey}`);
        }
    }, []);

    useEffect(() => {
        if (currentPage !== page) {
            setCurrentPage(page);

            if (pageVars.recordDescription === undefined) {
                navigate(`${page}/${record.descriptionKey}`);
            }
        }
    }, [page, currentPage]);

    if (isFetching || page !== currentPage) {
        return <LoadingDisplay isLoading={isFetching} />;
    }

    const pageSize = Math.min(NHLStats.length, 50);
    const showPagination = NHLStats.length > 50;
    const columns = utils.getColumnDataForPage(window.location, setVideoId);
    const urlData = queryString.parseUrl(restUrl);
    const sortBase = [{ id: 'index', desc: false }];
    const sortDataFromUrl = urlData.query.sort
        ? JSON.parse(urlData.query.sort).map((sortObj) => ({
              id: sortObj.property,
              desc: sortObj.direction === 'DESC' || sortObj.direction === 'DESC_CI',
          }))
        : {};
    const sortData = sortBase.concat(sortDataFromUrl);

    return (
        <>
            <VideoModal
                videoId={videoId}
                closeVideoModal={() => {
                    setVideoId(null);
                }}
            />
            <RecordTable
                data={NHLStats}
                record={record}
                recordSelectOptions={recordSelectOptions}
                recordTypeSelectOptions={recordTypeSelectOptions}
                templateKey={templateKey}
                selectedCategoryValue={selectedCategoryValue}
                selectedRecordTypeValue={selectedRecordTypeValue}
                pageSize={pageSize}
                showPagination={showPagination}
                columns={columns}
                sortData={sortData}
                setVideoId={setVideoId}
                options={options}
            />
        </>
    );
};

export default RecordTableDataProvider;
